import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

import {
  formatDate,
  formatCurrency,
  calcTransactionTotal,
} from '#common/utils';
import {
  formatTransactionItems,
  formatChargeInfo,
} from '#common/transaction-utils';
import EnhancedTable, { EnhancedTableData, EnhancedTableHeader } from '#components/EnhancedTable';

interface TransactionRow {
  items?: unknown[];
  amount?: number;
  paymentMethod?: string;
  transactionDate?: string | number | Date;
  notes?: string;
  [key: string]: unknown;
}

interface UserTransactionsProps {
  order: { additionalCharges?: unknown[]; [key: string]: unknown };
  transactions: EnhancedTableData;
}

export default function UserTransactions(props: UserTransactionsProps) {
  const {
    order,
    transactions,
  } = props;
  const { t } = useTranslation('order');

  const { additionalCharges } = order;

  const transactionsHeaders: EnhancedTableHeader[] = [{
    id: 'id',
    label: t('transactionsTable.number'),
    editable: false,
    renderer: (transaction, idx) => (idx + 1),
  }, {
    id: 'items',
    label: t('transactionsTable.items'),
    editable: false,
    renderer: (transaction: TransactionRow) => {
      const items = formatTransactionItems(
        transaction.items as Parameters<typeof formatTransactionItems>[0],
        order as unknown as Parameters<typeof formatTransactionItems>[1],
      );
      if (items.length === 0) {
        return '-';
      }
      return items.map((text, idx) => (<div key={idx}>{text}</div>));
    },
  }, {
    id: 'qty',
    label: t('transactionsTable.itemCount'),
    editable: false,
    renderer: (transaction: TransactionRow) => (transaction.items || []).length,
  }, {
    id: 'transactionDate',
    label: t('transactionsTable.transactionDate'),
    editable: false,
    renderer: (transaction: TransactionRow) => formatDate(
      transaction.transactionDate as string | number | Date,
    ),
  }, {
    id: 'paymentMethod',
    label: t('transactionsTable.paymentMethod'),
    editable: false,
    renderer: (transaction: TransactionRow) => transaction.paymentMethod || '-',
  }, {
    id: 'amount',
    label: t('transactionsTable.amount'),
    editable: false,
    renderer: (transaction: TransactionRow) => {
      const total = transaction.amount || calcTransactionTotal(
        transaction as unknown as Parameters<typeof calcTransactionTotal>[0],
        order as unknown as Parameters<typeof calcTransactionTotal>[1],
        additionalCharges as Parameters<typeof calcTransactionTotal>[2],
      );
      const chargeInfo = formatChargeInfo(
        transaction as unknown as Parameters<typeof formatChargeInfo>[0],
      );
      return `${formatCurrency(total)}${chargeInfo}`;
    },
  }, {
    id: 'notes',
    label: t('transactionsTable.notes'),
    editable: false,
    renderer: (transaction: TransactionRow) => transaction.notes || '-',
  }];

  return (
    <Box>
      <EnhancedTable
        name="transactions"
        headers={transactionsHeaders}
        {...transactions}
      />
    </Box>
  );
}

